/* eslint-disable max-lines */
/**
 * SubtitleDetailsDialog - Modal dialog showing detailed subtitle validation information
 */

import { calculateValidationMetrics } from "@lumi/lib";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  FileDown,
  FileText,
  XCircle,
} from "lucide-react";
import { useMemo, useState, type ReactElement } from "react";
import { toast } from "sonner";
import { normalizeLanguageName } from "../../../utils/languageMapping";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

interface ValidationMetrics {
  maxLinesPerCue?: number;
  maxLineLength?: number;
  readingSpeedIssues?: number;
  gapIssues?: number;
  durationIssues?: number;
}

interface SubtitleDetailsData {
  language: string;
  format: string;
  fileName?: string;
  /** Subtitle ID for download (admin only) */
  subtitleId?: string;
  /** R2 key for download (provider only) */
  r2Key?: string;
  /** Submission ID for provider download */
  submissionId?: string;
  processing?: {
    cueCount: number;
    sizeKB: number;
    wasConverted: boolean;
    wasNormalized: boolean;
    timecodeOffset: number;
  };
  validation?: {
    valid: boolean;
    errors: string[];
    /** Detailed warnings (individual cue messages) */
    warnings: string[];
    /** Grouped warnings for compact tooltip display */
    warningsSummary?: string[];
    /** Quality metrics */
    metrics?: ValidationMetrics;
  };
}

interface SubtitleDetailsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly data: SubtitleDetailsData | null;
}

function formatHeaderLines(data: SubtitleDetailsData): string[] {
  return [
    `Subtitle Validation Report`,
    `==========================`,
    ``,
    `Language: ${normalizeLanguageName(data.language)} (${data.language.toUpperCase()})`,
    `Format: ${data.format}`,
    ...(data.fileName ? [`File: ${data.fileName}`] : []),
    ``,
  ];
}

function formatProcessingLines(processing: SubtitleDetailsData["processing"]): string[] {
  if (!processing) return [];
  return [
    `Processing Info`,
    `---------------`,
    `Cue Count: ${processing.cueCount}`,
    `File Size: ${processing.sizeKB.toFixed(2)} KB`,
    ...(processing.wasConverted ? [`Converted: Yes (from SRT to VTT)`] : []),
    ...(processing.wasNormalized
      ? [`Normalized: Yes (shifted ${(processing.timecodeOffset / 1000).toFixed(2)}s)`]
      : []),
    ``,
  ];
}

function formatValidationLines(validation: SubtitleDetailsData["validation"]): string[] {
  if (!validation) return [];
  const lines: string[] = [`Validation: ${validation.valid ? "PASSED" : "FAILED"}`, `---------------`];

  if (validation.errors.length > 0) {
    lines.push(``, `Errors (${validation.errors.length}):`);
    for (const [i, err] of validation.errors.entries()) lines.push(`  ${i + 1}. ${err}`);
  }
  if (validation.warnings.length > 0) {
    lines.push(``, `Warnings (${validation.warnings.length}):`);
    for (const [i, warn] of validation.warnings.entries()) lines.push(`  ${i + 1}. ${warn}`);
  }
  if (validation.errors.length === 0 && validation.warnings.length === 0) {
    lines.push(`No errors or warnings found.`);
  }
  return lines;
}

function formatMetricsLines(metrics: ValidationMetrics | undefined): string[] {
  if (!metrics) return [];
  return [
    ``,
    `Quality Metrics`,
    `---------------`,
    `Max Lines/Cue: ${metrics.maxLinesPerCue}`,
    `Max Line Length: ${metrics.maxLineLength} chars`,
    `Reading Speed Issues: ${metrics.readingSpeedIssues}`,
    `Gap Issues: ${metrics.gapIssues}`,
    `Duration Issues: ${metrics.durationIssues}`,
  ];
}

function formatAsText(data: SubtitleDetailsData, metrics?: ValidationMetrics): string {
  return [
    ...formatHeaderLines(data),
    ...formatProcessingLines(data.processing),
    ...formatValidationLines(data.validation),
    ...formatMetricsLines(metrics),
  ].join("\n");
}

function getStatusStyle(isValid: boolean, hasWarnings: boolean): string {
  if (!isValid) return "bg-red-900/20 border-red-700/30";
  return hasWarnings ? "bg-yellow-900/20 border-yellow-700/30" : "bg-green-900/20 border-green-700/30";
}

function ValidationStatus({ data }: Readonly<{ data: SubtitleDetailsData }>): ReactElement {
  const isValid = data.validation?.valid ?? true;
  const hasWarnings = (data.validation?.warnings.length ?? 0) > 0;
  const bgClass = getStatusStyle(isValid, hasWarnings);

  if (!isValid) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded border ${bgClass}`}>
        <XCircle className="h-4 w-4 text-red-500" />
        <span className="text-sm text-red-300">Validation failed</span>
      </div>
    );
  }
  if (hasWarnings) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded border ${bgClass}`}>
        <span className="text-sm text-yellow-300">Valid with warnings</span>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded border ${bgClass}`}>
      <CheckCircle2 className="h-4 w-4 text-green-500" />
      <span className="text-sm text-green-300">Validation passed</span>
    </div>
  );
}

function ProcessingInfo({
  processing,
}: Readonly<{ processing: SubtitleDetailsData["processing"] }>): ReactElement | null {
  if (!processing) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-neutral-300">
        <FileText className="h-4 w-4" />
        <span>Processing Info</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="px-3 py-2">
          <div className="text-neutral-500 text-xs">Cue Count</div>
          <div className="text-white">{processing.cueCount}</div>
        </div>
        <div className="px-3 py-2">
          <div className="text-neutral-500 text-xs">File Size</div>
          <div className="text-white">{processing.sizeKB.toFixed(2)} KB</div>
        </div>
        {processing.wasConverted && <div className="col-span-2 px-3 py-2 text-white">Converted from SRT</div>}
        {processing.wasNormalized && (
          <div className="col-span-2 px-3 py-2 text-white">
            Shifted {(processing.timecodeOffset / 1000).toFixed(2)}s
          </div>
        )}
      </div>
    </div>
  );
}

/** Category patterns for issue classification */
const ISSUE_CATEGORY_PATTERNS: Array<{ patterns: string[]; category: string }> = [
  { patterns: ["reading speed", "chars/sec"], category: "Reading speed too fast" },
  { patterns: ["gap"], category: "Gap between cues too short" },
  { patterns: ["overlap"], category: "Cue overlaps" },
  { patterns: ["duration", "below"], category: "Duration too short" },
  { patterns: ["duration", "exceeds"], category: "Duration too long" },
  { patterns: ["lines", "max"], category: "Too many lines per cue" },
  { patterns: ["exceeds", "chars"], category: "Line too long" },
  { patterns: ["non-standard tags"], category: "Non-standard formatting" },
  { patterns: ["unclosed"], category: "Unclosed tags" },
];

/** Determine the category for an issue string */
function classifyIssue(item: string): string {
  for (const { patterns, category } of ISSUE_CATEGORY_PATTERNS) {
    const matchesAll = patterns.every((pattern) => item.includes(pattern));
    // Special case: "gap" pattern should not match items with "overlap"
    if (matchesAll && !(category === "Gap between cues too short" && item.includes("overlap"))) {
      return category;
    }
  }
  return "Other";
}

/** Group issues by category for summary display */
function groupIssuesByCategory(items: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();

  for (const item of items) {
    const category = classifyIssue(item);
    const existing = groups.get(category) ?? [];
    existing.push(item);
    groups.set(category, existing);
  }

  return groups;
}

function IssuesList({ items, type }: Readonly<{ items: string[]; type: "error" | "warning" }>): ReactElement | null {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  if (items.length === 0) return null;

  const isError = type === "error";
  const Icon = isError ? XCircle : AlertTriangle;
  const colorClass = isError ? "text-red-400" : "text-yellow-400";
  const grouped = groupIssuesByCategory(items);

  const toggleCategory = (category: string): void => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      <div className={`flex items-center gap-2 text-sm font-medium ${colorClass}`}>
        <Icon className="h-4 w-4" />
        <span>
          {isError ? "Errors" : "Warnings"} ({items.length})
        </span>
      </div>
      <div className="space-y-1">
        {[...grouped.entries()].map(([category, categoryItems]) => {
          const isExpanded = expandedCategories.has(category);

          return (
            <div key={category} className="border border-neutral-700/50 rounded overflow-hidden">
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left
                                    hover:bg-neutral-800/50 transition-colors
                                    ${isError ? "bg-red-900/10" : "bg-yellow-900/10"}`}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-3 w-3 flex-shrink-0" />
                )}
                <span className={`font-medium ${colorClass}`}>{category}</span>
                <span className="text-neutral-400">({categoryItems.length})</span>
              </button>
              {isExpanded && (
                <div className="border-t border-neutral-700/50">
                  {categoryItems.map((item) => (
                    <div
                      key={item}
                      className={`px-3 py-1.5 text-xs border-b border-neutral-800/50 last:border-b-0
                                                ${isError ? "text-red-300/80" : "text-yellow-300/80"}`}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Industry thresholds for subtitle quality */
const THRESHOLDS = {
  maxLinesPerCue: 2,
  maxLineLength: 42,
  readingSpeed: 21, // chars/sec
  minGap: 83, // ms (2 frames at 24fps)
  minDuration: 1000, // ms
  maxDuration: 7000, // ms
};

function MetricCell({
  label,
  value,
  threshold,
  isCount = false,
}: Readonly<{
  label: string;
  value?: number;
  threshold: number;
  isCount?: boolean;
}>): ReactElement {
  const hasValue = value !== undefined;
  const isOk = isCount ? (value ?? 0) === 0 : (value ?? 0) <= threshold;

  const getStatusColor = (): string => {
    if (!hasValue) return "text-neutral-500";
    return isOk ? "text-green-400" : "text-yellow-400";
  };
  const getBgColor = (): string => {
    if (!hasValue) return "";
    return isOk ? "bg-green-900/0" : "bg-yellow-900/0";
  };
  const getDisplayValue = (): string => {
    if (!hasValue) return "—";
    return isCount ? `${value} issues` : `${value}/${threshold}`;
  };

  return (
    <div className={`px-3 py-2 rounded ${getBgColor()}`}>
      <div className="text-neutral-500 text-xs">{label}</div>
      <div className={`text-sm font-medium ${getStatusColor()}`}>{getDisplayValue()}</div>
    </div>
  );
}

function QualityMetrics({ metrics }: Readonly<{ metrics?: ValidationMetrics }>): ReactElement | null {
  if (!metrics) return null;
  const hasAnyMetric = Object.values(metrics).some((v) => v !== undefined);
  if (!hasAnyMetric) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-neutral-300">
        <CheckCircle2 className="h-4 w-4" />
        <span>Quality Metrics</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
        <MetricCell label="Lines/Cue" value={metrics.maxLinesPerCue} threshold={THRESHOLDS.maxLinesPerCue} />
        <MetricCell label="Line Length" value={metrics.maxLineLength} threshold={THRESHOLDS.maxLineLength} />
        <MetricCell label="Reading Speed" value={metrics.readingSpeedIssues} threshold={0} isCount />
        <MetricCell label="Gap Issues" value={metrics.gapIssues} threshold={0} isCount />
        <MetricCell label="Duration Issues" value={metrics.durationIssues} threshold={0} isCount />
      </div>
      <div className="text-xs text-neutral-600 mt-1">
        Standards: ≤{THRESHOLDS.maxLinesPerCue} lines, ≤{THRESHOLDS.maxLineLength} chars, ≤{THRESHOLDS.readingSpeed}{" "}
        chars/sec, ≥{THRESHOLDS.minGap}ms gap, {THRESHOLDS.minDuration / 1000}-{THRESHOLDS.maxDuration / 1000}s duration
      </div>
    </div>
  );
}

/** Download a blob as a file */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Hook for dialog action handlers */
function useDialogActions(data: SubtitleDetailsData, metrics?: ValidationMetrics) {
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingVtt, setDownloadingVtt] = useState(false);

  const canDownloadVtt = Boolean(data.subtitleId) || Boolean(data.r2Key && data.submissionId);

  const handleCopy = async (): Promise<void> => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(formatAsText(data, metrics));
      toast.success("Report copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    } finally {
      setCopying(false);
    }
  };

  const handleDownload = (): void => {
    setDownloading(true);
    try {
      const content = formatAsText(data, metrics);
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const timestamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `subtitle-validation-${data.language}-${timestamp}.txt`);
      toast.success("Report downloaded");
    } catch {
      toast.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadVtt = async (): Promise<void> => {
    setDownloadingVtt(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
      if (data.subtitleId) {
        const response = await fetch(`${apiBaseUrl}/v1/subtitles/${data.subtitleId}/download.vtt`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error(`Download failed: ${response.status}`);
        downloadBlob(await response.blob(), `${data.language}.vtt`);
      } else if (data.r2Key && data.submissionId) {
        const response = await fetch(`${apiBaseUrl}/v1/provider/subtitles/read-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ submissionId: data.submissionId, r2Key: data.r2Key }),
        });
        if (!response.ok) throw new Error(`Failed to get download URL: ${response.status}`);
        const { readUrl } = (await response.json()) as { readUrl: string };
        window.open(readUrl, "_blank");
      }
      toast.success("VTT file downloaded");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download VTT");
    } finally {
      setDownloadingVtt(false);
    }
  };

  return { copying, downloading, downloadingVtt, canDownloadVtt, handleCopy, handleDownload, handleDownloadVtt };
}

interface ActionButtonsProps {
  readonly actions: ReturnType<typeof useDialogActions>;
}

function ActionButtons({ actions }: ActionButtonsProps): ReactElement {
  const { copying, downloading, downloadingVtt, canDownloadVtt, handleCopy, handleDownload, handleDownloadVtt } =
    actions;
  return (
    <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t border-neutral-800">
      {canDownloadVtt && (
        <button
          type="button"
          onClick={() => void handleDownloadVtt()}
          disabled={downloadingVtt}
          className="flex items-center gap-2 bg-transparent hover:text-lumi-accent focus-visible:text-lumi-accent px-3 py-2.5 text-sm shadow-none focus-visible:shadow-none hover:shadow-none"
          title="Download processed VTT file for editing"
        >
          <FileDown className="h-4 w-4" />
          {downloadingVtt ? "Downloading..." : "Download VTT"}
        </button>
      )}
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="flex items-center gap-2 bg-transparent hover:text-lumi-accent focus-visible:text-lumi-accent px-3 py-2.5 text-sm shadow-none focus-visible:shadow-none hover:shadow-none"
      >
        <Download className="h-4 w-4" />
        {downloading ? "Downloading..." : "Download"}
      </button>
      <button
        type="button"
        onClick={() => void handleCopy()}
        disabled={copying}
        className="flex items-center gap-2 bg-transparent hover:text-lumi-accent focus-visible:text-lumi-accent px-3 py-2.5 text-sm shadow-none focus-visible:shadow-none hover:shadow-none"
      >
        <Copy className="h-4 w-4" />
        {copying ? "Copying..." : "Copy Report"}
      </button>
      <DialogClose asChild>
        <button
          type="button"
          className="bg-transparent hover:text-lumi-accent focus-visible:text-lumi-accent px-3 py-2.5 text-sm shadow-none focus-visible:shadow-none hover:shadow-none"
        >
          Close
        </button>
      </DialogClose>
    </div>
  );
}

export function SubtitleDetailsDialog({
  open,
  onOpenChange,
  data,
}: Readonly<SubtitleDetailsDialogProps>): ReactElement | null {
  // Calculate metrics on-the-fly if not stored (for legacy subtitles)
  const computedMetrics = useMemo(() => {
    if (!data?.validation) return undefined;
    // Use stored metrics if available, otherwise calculate from errors/warnings
    if (data.validation.metrics) return data.validation.metrics;
    return calculateValidationMetrics({
      errors: data.validation.errors ?? [],
      warnings: data.validation.warnings ?? [],
    });
  }, [data?.validation]);

  // Must call hooks unconditionally
  const actions = useDialogActions(data ?? { language: "", format: "" }, computedMetrics);

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black/60 backdrop-blur-md border border-neutral-800 max-w-3xl max-h-[90vh] overflow-hidden scrollbar-hide flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base font-medium">
              <FileText className="h-5 w-5 text-neutral-400" />
              Subtitle Validation Details
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-neutral-500 mt-1">
            {normalizeLanguageName(data.language)} ({data.format}){data.fileName && <span> • {data.fileName}</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4 scrollbar-hide">
          <ValidationStatus data={data} />
          <ProcessingInfo processing={data.processing} />
          <QualityMetrics metrics={computedMetrics} />
          <IssuesList items={data.validation?.errors ?? []} type="error" />
          <IssuesList items={data.validation?.warnings ?? []} type="warning" />
        </div>
        <ActionButtons actions={actions} />
      </DialogContent>
    </Dialog>
  );
}
